/**
 * EntityCardList Component
 * Container for rendering a list of expandable entity cards (ad groups or asset groups)
 */

import React from 'react';
import type { EntityCardListProps } from './types';
import { EntityCard } from './EntityCard';
import { getEntityId, getEntityListLabel } from './utils';

export function EntityCardList({
  entities,
  entityType,
  expandedId,
  onExpandEntity,
  activeTabByEntity,
  onTabChange,
  campaign,
  selectedLandingPages,
  rdaCache,
  isLoadingRDAs,
  demandGenCache,
  isLoadingDemandGenAds,
  onEditRSA,
  onEditRDA,
  onEditDemandGenAd,
  onCreateDemandGenAd,
  editingRSA,
  editingRDA,
  editingDemandGenAd,
  memberUuid,
  getRDAInitialData,
  getDemandGenAdInitialData,
  onEditorSaved,
  onEditorCancel,
}: EntityCardListProps) {
  const campaignType = campaign.campaign_type || '';
  const listLabel = getEntityListLabel(campaignType, entities.length);

  if (entities.length === 0) {
    return (
      <div className="p-6 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          {entityType === 'asset_group' ? 'Asset Groups' : 'Ad Groups'}
        </h3>
        <p className="text-sm text-gray-500">
          {entityType === 'asset_group'
            ? 'No asset groups found in this campaign.'
            : 'No ad groups found in this campaign.'}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Navigate to this campaign from the Account page to load the full campaign structure.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Section Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">
          {listLabel}
        </h3>
      </div>

      {/* Entity Cards */}
      <div className="p-3 pb-4 space-y-2">
        {entities.map((entity) => {
          const entityId = getEntityId(entity, entityType);
          const isExpanded = expandedId === entityId;
          const activeTab = activeTabByEntity[entityId] || 'keywords';

          return (
            <EntityCard
              key={entityId}
              entity={entity}
              entityType={entityType}
              isExpanded={isExpanded}
              onToggle={() => onExpandEntity(isExpanded ? null : entityId)}
              activeTab={activeTab}
              onTabChange={(tab) => onTabChange(entityId, tab)}
              campaign={campaign}
              selectedLandingPages={selectedLandingPages}
              rdaCache={rdaCache}
              isLoadingRDAs={isLoadingRDAs}
              demandGenCache={demandGenCache}
              isLoadingDemandGenAds={isLoadingDemandGenAds}
              onEditRSA={onEditRSA}
              onEditRDA={onEditRDA}
              onEditDemandGenAd={onEditDemandGenAd}
              onCreateDemandGenAd={onCreateDemandGenAd}
              editingRSA={editingRSA}
              editingRDA={editingRDA}
              editingDemandGenAd={editingDemandGenAd}
              memberUuid={memberUuid}
              getRDAInitialData={getRDAInitialData}
              getDemandGenAdInitialData={getDemandGenAdInitialData}
              onEditorSaved={onEditorSaved}
              onEditorCancel={onEditorCancel}
            />
          );
        })}
      </div>
    </div>
  );
}

export default EntityCardList;
